import React, { useState, useCallback } from 'react';
import cn from 'classnames';
import pick from 'lodash/pick';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';

import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { getActionItems } from 'Shared/selectors/meetingItems';
import WpProgressBar from 'Wpds/components/progress/WpProgressBar';
import PageContext from 'Shared/contexts/PageContext';
import { pageContext } from 'Shared/constants/constants';
import { getSortedItems } from 'Shared/utils/sorting';
import { addItem } from 'Shared/actions/todos';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';

import ThreeSixtySharePageActionItemsList from './ThreeSixtySharePageActionItemsList/ThreeSixtySharePageActionItemsList';
import ThreeSixtySharePageActionItemsEmpty from './ThreeSixtySharePageActionItemsEmpty/ThreeSixtySharePageActionItemsEmpty';

import './ThreeSixtySharePageActionItems.scss';

type Props = {
  reviewSlug: string;
  workspaceSlug: string;
};

const ItemWhiteList = Object.freeze([
  'id',
  'uuid',
  'completed',
  'parentUuid',
  'workspacePath',
  'parentText',
  'isHighPriority',
  'workspaceId',
  'assignmentAvatar',
]);

const sortingMap = {
  actionItems: 'default',
  owner: 'default',
  dueDate: 'default',
};

const ThreeSixtySharePageActionItems = ({ reviewSlug, workspaceSlug }: Props) => {
  const dispatch = useDispatch();
  const [selectedSort, setSelectedSort] = useState(sortingMap);
  const actionItems = useDeepEqualSelector(state => {
    const items = getActionItems(state).filter(
      ({ reviewSlug: meetingItemReviewSlug }) => meetingItemReviewSlug === reviewSlug
    );
    const sortedItems = getSortedItems({
      items,
      selectedSort,
      withHighPriority: true,
      positionField: 'position',
    });
    return sortedItems.map(actionItem => pick(actionItem, ItemWhiteList));
  });
  const actionItemsLength = actionItems.length;
  const completedActionItemsLength = actionItems.filter(({ completed }) => completed).length;
  const actionItemsPresent = Boolean(actionItemsLength);

  const actionItemsStatus = actionItemsPresent
    ? `${completedActionItemsLength}/${actionItems.length} action items complete`
    : 'No Action Items';

  const progressBarPercentage = actionItemsLength === 0 ? 0 : (completedActionItemsLength * 100) / actionItemsLength;

  const handleSelectedItemUpdate = useCallback(
    itemUuid => dispatch(updateSelectedUuids(itemUuid, { isOneSelected: true })),
    [dispatch]
  );

  const handleAddItemClick = useCallback(() => {
    const itemUuid = uuidv4();
    dispatch(addItem({ itemUuid, reviewSlug, workspaceId: workspaceSlug }));
    handleSelectedItemUpdate(itemUuid);
  }, [dispatch, actionItemsLength, reviewSlug, workspaceSlug, handleSelectedItemUpdate]);

  return (
    <PageContext.Provider value={pageContext.REVIEW_SHARE_PAGE}>
      <div className="ThreeSixtySharePageActionItems">
        <div className={cn('ThreeSixtySharePageActionItems__header')}>
          <span>Action Items</span>
          <span className="ThreeSixtyShareActionItems__header__status">{actionItemsStatus}</span>
        </div>
        {actionItemsPresent ? (
          <>
            <WpProgressBar percent={progressBarPercentage} />
            <ThreeSixtySharePageActionItemsList
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
              onAddItemClick={handleAddItemClick}
              reviewSlug={reviewSlug}
              actionItems={actionItems}
            />
          </>
        ) : null}
        {!actionItemsPresent && <ThreeSixtySharePageActionItemsEmpty onAddItemClick={handleAddItemClick} />}
      </div>
    </PageContext.Provider>
  );
};

export default ThreeSixtySharePageActionItems;
